// client/src/pages/admin/PerformanceReports.tsx
import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Space,
  Typography,
  Progress,
  Tag,
  Alert,
  Tabs,
  Badge,
  Statistic,
  DatePicker
} from 'antd';
import {
  TrophyOutlined,
  UserOutlined,
  CarOutlined,
  RiseOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
  DownloadOutlined,
  StarOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { reportService, type TopDriver, type TopVehicle } from '../../services/reportService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AdminPerformanceReports: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch performance report
  const { data: performanceData, isLoading } = useQuery({
    queryKey: ['performanceReport', dateRange],
    queryFn: () => {
      const start = dateRange?.[0]?.toISOString();
      const end = dateRange?.[1]?.toISOString();
      return reportService.getPerformanceReport(start, end);
    },
  });

  // Fetch fleet KPIs
  const { data: kpis } = useQuery({
    queryKey: ['fleetKPIs'],
    queryFn: () => reportService.getFleetKPIs(),
  });

  // Fetch leaderboard
  const { data: leaderboard } = useQuery({
    queryKey: ['performanceLeaderboard'],
    queryFn: () => reportService.getPerformanceLeaderboard(10),
  });

  const topDrivers = leaderboard?.top_drivers || [];
  const topVehicles = leaderboard?.top_vehicles || [];

  const getCompletionRateColor = (rate: number) => {
    if (rate >= 95) return '#52c41a';
    if (rate >= 85) return '#1890ff';
    if (rate >= 75) return '#fa8c16';
    return '#ff4d4f';
  };

  const driversColumns: ColumnsType<TopDriver> = [
    {
      title: 'Rang',
      key: 'rank',
      width: 60,
      render: (_, __, index) => (
        <Badge
          count={index + 1}
          style={{
            backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#1890ff'
          }}
        />
      ),
    },
    {
      title: 'Conducteur',
      key: 'driver',
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {record.first_name} {record.last_name}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Missions',
      key: 'missions',
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.completed_missions}</strong> / {record.total_missions}
          </div>
          <Progress
            percent={record.completion_rate}
            size="small"
            strokeColor={getCompletionRateColor(record.completion_rate)}
            format={(percent) => `${percent}%`}
          />
        </div>
      ),
    },
    {
      title: 'Taux de réussite',
      dataIndex: 'completion_rate',
      key: 'completion_rate',
      sorter: (a, b) => a.completion_rate - b.completion_rate,
      render: (rate: number) => (
        <Tag color={getCompletionRateColor(rate)}>
          {rate.toFixed(1)}%
        </Tag>
      ),
    },
    {
      title: 'Consommation moy.',
      dataIndex: 'avg_fuel_consumption',
      key: 'avg_fuel_consumption',
      render: (consumption: number) =>
        consumption ? `${consumption.toFixed(1)} L/100km` : '-',
    },
    {
      title: 'Distance totale',
      dataIndex: 'total_distance_km',
      key: 'total_distance_km',
      render: (distance: number) =>
        distance ? `${distance.toLocaleString()} km` : '-',
    },
  ];

  const vehiclesColumns: ColumnsType<TopVehicle> = [
    {
      title: 'Rang',
      key: 'rank',
      width: 60,
      render: (_, __, index) => (
        <Badge
          count={index + 1}
          style={{
            backgroundColor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#1890ff'
          }}
        />
      ),
    },
    {
      title: 'Véhicule',
      key: 'vehicle',
      render: (_, record) => (
        <Space>
          <CarOutlined />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.license_plate}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.brand} {record.model}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Missions',
      key: 'missions',
      render: (_, record) => (
        <div>
          <div>
            <strong>{record.completed_missions}</strong> / {record.total_missions}
          </div>
        </div>
      ),
    },
    {
      title: 'Consommation moy.',
      dataIndex: 'avg_fuel_consumption',
      key: 'avg_fuel_consumption',
      sorter: (a, b) => (a.avg_fuel_consumption || 0) - (b.avg_fuel_consumption || 0),
      render: (consumption: number) => (
        <Tag color={consumption < 8 ? 'green' : consumption < 10 ? 'orange' : 'red'}>
          {consumption ? `${consumption.toFixed(1)} L/100km` : '-'}
        </Tag>
      ),
    },
    {
      title: 'Coût total',
      dataIndex: 'total_operating_cost',
      key: 'total_operating_cost',
      render: (cost: number) =>
        cost ? `${cost.toFixed(2)}€` : '-',
    },
    {
      title: 'Coût/km',
      dataIndex: 'cost_per_km',
      key: 'cost_per_km',
      render: (cost: number) =>
        cost ? `${cost.toFixed(3)}€/km` : '-',
    },
  ];

  // Handle loading and error states
  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <Card loading={true} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>
            <DashboardOutlined /> Rapports de Performance
          </Title>
        </Col>
        <Col>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              format="DD/MM/YYYY"
            />
            <Button icon={<DownloadOutlined />}>
              Exporter
            </Button>
          </Space>
        </Col>
      </Row>

      {/* KPIs Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Véhicules actifs"
              value={kpis?.active_vehicles || 0}
              suffix={`/ ${kpis?.total_vehicles || 0}`}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Conducteurs actifs"
              value={kpis?.active_drivers || 0}
              suffix={`/ ${kpis?.total_drivers || 0}`}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Missions ce mois"
              value={kpis?.missions_this_month || 0}
              prefix={<CheckCircleOutlined />}
              suffix={`(${kpis?.completed_missions_this_month || 0} terminées)`}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Taux de réussite"
              value={kpis?.mission_completion_rate || 0}
              precision={1}
              suffix="%"
              prefix={<TrophyOutlined />}
              valueStyle={{
                color: getCompletionRateColor(kpis?.mission_completion_rate || 0)
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Financial Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût carburant total"
              value={kpis?.total_fuel_cost || 0}
              precision={2}
              suffix="€"
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût maintenance total"
              value={kpis?.total_maintenance_cost || 0}
              precision={2}
              suffix="€"
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Distance totale"
              value={kpis?.total_distance_km || 0}
              suffix="km"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Taux d'utilisation"
              value={kpis?.vehicle_utilization_rate || 0}
              precision={1}
              suffix="%"
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Alerts */}
      {performanceData?.fleet_overview?.alerts && (
        <>
          {(performanceData.fleet_overview.alerts.vehicles_needing_maintenance > 0 ||
            performanceData.fleet_overview.alerts.drivers_low_activity > 0) && (
            <Alert
              message="Alertes de performance"
              description={
                <div>
                  {performanceData.fleet_overview.alerts.vehicles_needing_maintenance > 0 && (
                    <div>
                      ⚠️ {performanceData.fleet_overview.alerts.vehicles_needing_maintenance} véhicule(s)
                      nécessitent une maintenance
                    </div>
                  )}
                  {performanceData.fleet_overview.alerts.drivers_low_activity > 0 && (
                    <div>
                      📉 {performanceData.fleet_overview.alerts.drivers_low_activity} conducteur(s) avec
                      faible activité
                    </div>
                  )}
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}
        </>
      )}

      {/* Tabs for detailed reports */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={
              <span>
                <TrophyOutlined />
                Top Conducteurs
              </span>
            }
            key="drivers"
          >
            <Table
              columns={driversColumns}
              dataSource={topDrivers}
              rowKey="driver_id"
              pagination={false}
              loading={isLoading}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <StarOutlined />
                Top Véhicules
              </span>
            }
            key="vehicles"
          >
            <Table
              columns={vehiclesColumns}
              dataSource={topVehicles}
              rowKey="vehicle_id"
              pagination={false}
              loading={isLoading}
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <DashboardOutlined />
                Vue d'ensemble
              </span>
            }
            key="overview"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card title="Statistiques Missions" bordered={false}>
                  {performanceData?.mission_statistics && (
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>Total:</Text> {performanceData.mission_statistics.total_missions}
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>Terminées:</Text>{' '}
                        <Tag color="green">{performanceData.mission_statistics.completed}</Tag>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>En cours:</Text>{' '}
                        <Tag color="blue">{performanceData.mission_statistics.in_progress}</Tag>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>En attente:</Text>{' '}
                        <Tag color="orange">{performanceData.mission_statistics.pending}</Tag>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>Annulées:</Text>{' '}
                        <Tag color="red">{performanceData.mission_statistics.cancelled}</Tag>
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <Text strong>Durée moyenne:</Text>{' '}
                        {performanceData.mission_statistics.avg_duration_hours
                          ? `${performanceData.mission_statistics.avg_duration_hours.toFixed(1)}h`
                          : '-'}
                      </div>
                    </div>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="Meilleurs Performers" bordered={false}>
                  {performanceData?.fleet_overview?.top_performers && (
                    <div>
                      {performanceData.fleet_overview.top_performers.best_driver && (
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong>🏆 Meilleur conducteur:</Text>
                          <div style={{ marginTop: '8px' }}>
                            {performanceData.fleet_overview.top_performers.best_driver.name}
                            <Tag color="gold" style={{ marginLeft: '8px' }}>
                              {performanceData.fleet_overview.top_performers.best_driver.completion_rate}%
                            </Tag>
                          </div>
                        </div>
                      )}
                      {performanceData.fleet_overview.top_performers.most_efficient_vehicle && (
                        <div style={{ marginBottom: '16px' }}>
                          <Text strong>⭐ Véhicule le plus efficace:</Text>
                          <div style={{ marginTop: '8px' }}>
                            {performanceData.fleet_overview.top_performers.most_efficient_vehicle.license_plate}
                            <Tag color="green" style={{ marginLeft: '8px' }}>
                              {performanceData.fleet_overview.top_performers.most_efficient_vehicle.avg_consumption?.toFixed(
                                1
                              )}{' '}
                              L/100km
                            </Tag>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default AdminPerformanceReports;
